// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useState } from 'react';

const SITE_URL = window.location.origin;

const SharePanel = ({ score, gameTitle, gameMode, screenshotDataUrl, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const modeLabel = {
    run: 'Runner', fly: 'Flight', combat: 'Combat',
    race: 'Racer', openworld: 'Open World', puzzle: 'Puzzler',
  }[gameMode] || 'Game';

  const shareText = `I scored ${score} points in "${gameTitle || 'Planet Q'}" (${modeLabel} Mode) on Planet Q AI Video Games! Can you beat my score?`;
  const shareUrl = SITE_URL;
  const hashtags = 'PlanetQ,AIGames,Gaming';

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&hashtags=${hashtags}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(url, '_blank');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = `${shareText}\n${shareUrl}`;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const downloadScreenshot = () => {
    if (!screenshotDataUrl) return;
    setDownloading(true);
    const link = document.createElement('a');
    link.href = screenshotDataUrl;
    link.download = `PlanetQ_${(gameTitle || 'game').replace(/\s+/g, '_')}_Score${score}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setDownloading(false), 1000);
  };

  const nativeShare = async () => {
    if (!navigator.share) return;
    const shareData = { title: 'Planet Q AI Video Games', text: shareText, url: shareUrl };
    // Try sharing with image if available
    if (screenshotDataUrl && navigator.canShare) {
      try {
        const res = await fetch(screenshotDataUrl);
        const blob = await res.blob();
        const file = new File([blob], 'planetq-score.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
      } catch {}
    }
    try { await navigator.share(shareData); } catch {}
  };

  const btnBase = "flex items-center justify-center gap-2 py-2.5 px-4 rounded-full font-bold text-sm uppercase tracking-wider transition-all hover:scale-105 active:scale-95";

  return (
    <div className="mt-4 w-full max-w-sm mx-auto" data-testid="share-panel">
      <p className="text-center text-white/50 text-xs uppercase tracking-[0.2em] mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
        Share Your Score
      </p>

      <div className="grid grid-cols-2 gap-2">
        {/* Twitter/X */}
        <button
          data-testid="share-twitter"
          onClick={shareToTwitter}
          className={btnBase}
          style={{ background: 'rgba(29,155,240,0.2)', border: '1px solid rgba(29,155,240,0.4)', color: '#1DA1F2', fontFamily: "'Rajdhani', sans-serif" }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
          X / Twitter
        </button>

        {/* Facebook */}
        <button
          data-testid="share-facebook"
          onClick={shareToFacebook}
          className={btnBase}
          style={{ background: 'rgba(24,119,242,0.2)', border: '1px solid rgba(24,119,242,0.4)', color: '#1877F2', fontFamily: "'Rajdhani', sans-serif" }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          Facebook
        </button>

        {/* WhatsApp */}
        <button
          data-testid="share-whatsapp"
          onClick={shareToWhatsApp}
          className={btnBase}
          style={{ background: 'rgba(37,211,102,0.2)', border: '1px solid rgba(37,211,102,0.4)', color: '#25D366', fontFamily: "'Rajdhani', sans-serif" }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          WhatsApp
        </button>

        {/* Copy Link */}
        <button
          data-testid="share-copy-link"
          onClick={copyToClipboard}
          className={btnBase}
          style={{ background: copied ? 'rgba(0,240,255,0.25)' : 'rgba(255,255,255,0.08)', border: `1px solid ${copied ? 'rgba(0,240,255,0.5)' : 'rgba(255,255,255,0.2)'}`, color: copied ? '#00F0FF' : 'rgba(255,255,255,0.7)', fontFamily: "'Rajdhani', sans-serif" }}
        >
          {copied ? (
            <><svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
          ) : (
            <><svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>Copy Link</>
          )}
        </button>
      </div>

      {/* Screenshot Download */}
      {screenshotDataUrl && (
        <button
          data-testid="share-download-screenshot"
          onClick={downloadScreenshot}
          className={`${btnBase} w-full mt-2`}
          style={{ background: 'rgba(255,215,0,0.15)', border: '1px solid rgba(255,215,0,0.3)', color: '#FFD700', fontFamily: "'Rajdhani', sans-serif" }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {downloading ? 'Downloading...' : 'Download Screenshot'}
        </button>
      )}

      {/* Native Share (mobile) */}
      {navigator.share && (
        <button
          data-testid="share-native"
          onClick={nativeShare}
          className={`${btnBase} w-full mt-2`}
          style={{ background: 'rgba(0,240,255,0.1)', border: '1px solid rgba(0,240,255,0.25)', color: '#00F0FF', fontFamily: "'Rajdhani', sans-serif" }}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          Share
        </button>
      )}
    </div>
  );
};

export default SharePanel;
